import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import Basket from "assets/img/svg/basket";
import Pdf from "assets/img/svg/pdf";
import { H3 } from "helpers/TypographyComponents";
import { getStatementFilename } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { Agreement } from "store/agreementsStore";
import { fetchAgreementPreview } from "store/agreementsStore";
import { downloadDocument } from "store/bpTermStore";
import type { ITerm } from "store/managementStore";
import type { ICompany } from "store/settingsStore";
import type { RootState } from "store/store";
import { useAppDispatch } from "store/store";
import { deleteTermDocument, uploadDocument } from "store/termStore";

import DeleteModal from "../DeleteModal";
import StyledButton from "../StyledButton";
import DropZoneCSV from "../UploadCsv";
import AgreementStatus from "./AgreementStatus";

type TProps = {
    term: ITerm;
    company: ICompany | undefined;
    agreement?: Agreement | undefined;
};

const TermDetail = ({ term, company, agreement }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { user } = useAuthenticator((context) => [context.user]);
    const userRole = user.attributes ? user.attributes["custom:accountType"] : "";
    const { getCompanySettings } = useCompanySettings();
    const usesAgreements = getCompanySettings("uses_agreements");
    const hasAgreement = !!agreement;

    const termData = useSelector((state: RootState) => state.term.term as ITerm);

    const isManuallyUploaded = termData.storageId?.startsWith("crealo:") ?? false;

    const [file, setFile] = useState<any>();
    const [fileName, setFileName] = useState<string>();
    const [askReupload, setAskReupload] = useState<boolean>(false);
    const [openDeleteContractModal, setOpenDeleteContractModal] = useState(false);

    const handleFileInput = (e: any) => {
        if (e.target.files[0]) {
            setFile(e.target.files[0]);
            setFileName(e.target.files[0].name ? e.target.files[0].name.split(".")[0] : "");
            const formData = new FormData();
            formData.append("file", e.target.files[0], fileName || e.target.files[0].name);
            dispatch(uploadDocument({ id: term.termId as number, file: formData })).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setAskReupload(false);
                }
            });
        }
    };

    const handleDownload = () => {
        if (term.originalFilename && !hasAgreement) {
            dispatch(
                downloadDocument({
                    id: term.termId as number,
                    userRole,
                    file: {
                        mimetype: term.originalFilemime ?? "application/pdf",
                        name: term.originalFilename,
                    },
                }),
            );
        }
        if (hasAgreement) {
            const filename = getStatementFilename(
                term?.author ?? "",
                term?.title ?? "",
                t("Summary.Contract"),
                new Date(),
            );
            dispatch(
                fetchAgreementPreview({
                    filename,
                    agreementId: +(agreement.id ?? -1),
                }),
            );
        }
    };

    const handleUpload = () => {
        if (file && file.length > 0) {
            const formData = new FormData();
            formData.append("file", file[0], file[0].name);
            dispatch(uploadDocument({ id: term.termId as number, file: formData })).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setAskReupload(false);
                }
            });
        }
    };

    const handleDeletePDF = async () => {
        if (termData?.termId) {
            await dispatch(deleteTermDocument({ termId: termData?.termId }));
            setOpenDeleteContractModal(false);
            setFile([]);
        }
    };

    return (
        <>
            <DeleteModal
                open={openDeleteContractModal}
                onClose={() => setOpenDeleteContractModal(false)}
                onDelete={() => handleDeletePDF()}
                elementTitle={t("ShowDetails.TheContract")}
                description={term.originalFilename as string}
            />
            <div className="term-detail-container">
                <div className="detail-documents">
                    <div className="header">
                        {!usesAgreements || !!termData?.originalFilename ? (
                            <H3>{t("ShowDetails.OriginalTerm")}</H3>
                        ) : (
                            <></>
                        )}
                    </div>
                    {!!termData?.originalFilename && !askReupload && !hasAgreement ? (
                        <div className="document-uploaded" style={{ flex: "1" }}>
                            <button className="download" onClick={() => handleDownload()}>
                                <Pdf />
                                {term?.originalFilename?.split(".")[0]}
                            </button>
                            {isManuallyUploaded && <Basket onClick={() => setOpenDeleteContractModal(true)} />}
                        </div>
                    ) : hasAgreement ? (
                        <AgreementStatus handleDownload={handleDownload} agreement={agreement} />
                    ) : (
                        <div style={{ textAlign: "center" }}>
                            <DropZoneCSV
                                setFile={setFile}
                                file={file}
                                acceptedFilesTypeList={{ "application/pdf": [".pdf"] }}
                            />
                            {!!file?.length && (
                                <StyledButton
                                    sx={{ marginTop: "1rem" }}
                                    buttonType="primary"
                                    onClick={() => handleUpload()}
                                    fullWidth
                                >
                                    {t("ShowDetails.Save")}
                                </StyledButton>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default TermDetail;
